import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/styles';
import { forwardRef, useContext, useState } from 'react';
import copyIcon from '../../../assets/img/copy-regular.svg';
import docsContext from '../../../context/DocsContext';
import theme from '../../../themes/theme';
import {
    docsSearchValue,
    fetchStatuses,
    fileExporterTypes,
    listTypes,
    listTypesNames,
    localStorageKey,
    testListVersion,
} from '../../../utils/constants';
import CountPipelineDocs from '../../CountPipelineDocs/CountPipelineDocs';
import AutocompleteBar from '../../Tables/AutocompleteBar';
import TableSearchBar from '../../Tables/TableSearchBar';
import ColumnsSettingsItem from './ColumnsSettingsItem/ColumnsSettingsItem';
import FileExporter from './FileExporter/FileExporter';

const useStyles = makeStyles({
    waterpipeSettingsPanel: {
        position: 'relative',
        borderBottom: 'none',
        background: theme.colors.panelBackground,
        border: `1px solid ${theme.colors.border}`,
        display: 'flex',
        [theme.mediaBreakpoints.md_waterpipe]: {
            flexDirection: 'column',
        },
    },
    listSettingsPanel: {
        position: 'relative',
        borderBottom: 'none',
        background: theme.colors.panelBackground,
        border: `1px solid ${theme.colors.border}`,
        display: 'flex',
        [theme.mediaBreakpoints.md]: {
            flexDirection: 'column',
        },
    },
    waterpipeSettingsOptions: {
        display: 'flex',
        justifyContent: 'space-between',
        alignSelf: 'end',
        flex: 1,
        width: '100%',
        [theme.mediaBreakpoints.md_waterpipe]: {
            flexWrap: 'wrap',
            justifyContent: 'center',
        },
    },
    listSettingsOptions: {
        display: 'flex',
        justifyContent: 'right',
        alignSelf: 'end',
        flex: 1,
        width: '100%',
        [theme.mediaBreakpoints.md]: {
            flexWrap: 'wrap',
            justifyContent: 'center',
        },
    },
    waterpipeTableName: {
        fontWeight: theme.font.weight.bold,
        marginLeft: 25,
        marginRight: 25,
        display: 'flex',
        alignItems: 'center',
        [theme.mediaBreakpoints.md_waterpipe]: {
            justifyContent: 'center',
            marginLeft: 0,
        },
    },
    listTableName: {
        fontWeight: theme.font.weight.bold,
        marginLeft: 25,
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        [theme.mediaBreakpoints.md]: {
            justifyContent: 'center',
            marginLeft: 0,
        },
    },
    filters: {
        display: 'flex',
        justifyContent: 'right',
        alignSelf: 'center',
        marginRight: '1rem',
        gap: '2rem',
    },
    reset: {
        alignSelf: 'center',
    },
    filterWrapper: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        marginRight: '2rem',
        [theme.mediaBreakpoints.sm_waterpipe]: {
            alignItems: 'center',
            flexDirection: 'column',
        },
    },
    processingText: {
        fontSize: '1rem',
        display: 'flex',
        alignItems: 'center',
    },
    panelNote: {
        fontSize: '1rem',
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'center',
    },
    copyButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 25px 0 25px',
        cursor: 'pointer',
        border: 'none',
        background: 'transparent',
        padding: 0,
        fontFamily: theme.font.family,
        fontSize: '1rem',
        userSelect: 'none',
        transition: 'opacity 0.15s ease',
    },
    copyButtonFlash: {
        opacity: 0.2,
    },
    copyIcon: {
        width: 30,
        height: 30,
        marginRight: 5,
        pointerEvents: 'none',
        userSelect: 'none',
    },
});

const SettingsPanel = (props, ref) => {
    const {
        columnsDefs,
        setColumnHide,
        gridApi,
        listType,
        hideExport,
        hideColumnsSettings,
        hideFilters,
        copyData,
        listVersion,
        match,
        settingsPanelPosition,
        tableHeaderName,
        isTable,
        isFetching,
        hidePackageIdField,
        hideCommunicatorField,
        hideReportTypeField,
        hideResetButton,
        hideCountField,
        countEndpoint,
        countParams,
        countFilter,
        panelNote,
        failedColumns,
    } = props;

    const {
        filterWrapper,
        filters,
        reset,
        listSettingsPanel,
        waterpipeSettingsPanel,
        waterpipeSettingsOptions,
        listSettingsOptions,
        waterpipeTableName,
        listTableName,
        processingText,
        copyButton,
        copyButtonFlash,
        copyIcon: copyIconClass,
        panelNote: panelNoteClass,
    } = useStyles();

    const [isDataProcessing, setIsDataProcessing] = useState(false);
    const [isFlashing, setIsFlashing] = useState(false);

    const handleCopy = async () => {
        if (!gridApi || typeof gridApi.getRenderedNodes !== 'function') {
            return;
        }
        if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
            return;
        }
        const numbers = gridApi
            .getRenderedNodes()
            .map(node => node.data?.[copyData])
            .filter(value => value !== undefined && value !== null);
        try {
            await navigator.clipboard.writeText(numbers.join(','));
            setIsFlashing(true);
            setTimeout(() => setIsFlashing(false), 300);
        } catch (_error) {
            setIsFlashing(false);
        }
    };

    const docsProps = useContext(docsContext);

    const getListName = () => {
        if (listType === listTypes.WATERPIPE_LIST) return `${tableHeaderName} `;
        if (listType === listTypes.SERIAL_NUMBERS) return 'Unikaty';
        const typeName =
            listType === listTypes.TEST_LIST ? listTypesNames.TEST_LIST : listTypesNames.PRODUCTION_LIST;
        const versionSuffix = listVersion === testListVersion.V2 ? ' V2' : '';
        return `Zestawienie ${typeName}${versionSuffix}`;
    };

    const LIST_NAME = getListName();

    return (
        <div className={hideFilters ? listSettingsPanel : waterpipeSettingsPanel} ref={ref}>
            <p className={hideFilters ? listTableName : waterpipeTableName}>{LIST_NAME}</p>
            <div className={hideFilters ? listSettingsOptions : waterpipeSettingsOptions}>
                {!hideFilters && (
                    <div className={filterWrapper}>
                        {!hideCountField && (
                            <CountPipelineDocs
                                match={match}
                                history={history}
                                reportType={docsProps.reportType}
                                countEndpoint={countEndpoint}
                                countParams={countParams}
                                searchPhrase={countFilter}
                            />
                        )}
                        {panelNote && <p className={panelNoteClass}>{panelNote}</p>}
                        <div className={filters}>
                            {!hideReportTypeField && (
                                <AutocompleteBar
                                    label="Typ raportu"
                                    options={docsProps.options}
                                    handleFilter={docsProps.handleFilter}
                                    value={docsProps.reportType}
                                    checkType={docsProps.reportType}
                                    localStorageKey={localStorageKey.DOCS_REPORT_TYPE}
                                    isTable
                                />
                            )}
                            {!hideCommunicatorField && (
                                <TableSearchBar
                                    placeholder="Komunikator"
                                    searchValue={docsSearchValue.COMMUNICATOR}
                                    clearSearchField={docsProps.clearSearchField}
                                    clearedSearchField={docsProps.clearedSearchField}
                                    handleSearch={docsProps.handleSearchCommunicator}
                                    isTable={isTable}
                                />
                            )}
                            {!hidePackageIdField && (
                                <TableSearchBar
                                    placeholder="ID pakietu"
                                    searchValue={docsSearchValue.PACKAGE_ID}
                                    clearSearchField={docsProps.clearSearchField}
                                    clearedSearchField={docsProps.clearedSearchField}
                                    handleSearch={docsProps.handleSearchPackageId}
                                    isTable={isTable}
                                />
                            )}
                            {!hideResetButton && (
                                <Button
                                    className={reset}
                                    variant="contained"
                                    color="secondary"
                                    onClick={docsProps.handleResetFieldsButton}>
                                    reset
                                </Button>
                            )}
                        </div>
                    </div>
                )}
                {isDataProcessing && <p className={processingText}>{fetchStatuses.exporting}</p>}
                {copyData !== undefined && !isFetching && (
                    <button
                        className={`${copyButton}${isFlashing ? ` ${copyButtonFlash}` : ''}`}
                        onClick={handleCopy}>
                        <img src={copyIcon} className={copyIconClass} alt="copy" />
                        Skopiuj numery
                    </button>
                )}
                {!hideExport && !isFetching && (
                    <>
                        <FileExporter
                            gridApi={gridApi}
                            type={fileExporterTypes.XLS}
                            listName={LIST_NAME}
                            failedColumns={failedColumns}
                            setIsDataProcessing={setIsDataProcessing}
                        />
                        <FileExporter
                            gridApi={gridApi}
                            type={fileExporterTypes.CSV}
                            listName={LIST_NAME}
                            failedColumns={failedColumns}
                            setIsDataProcessing={setIsDataProcessing}
                        />
                    </>
                )}
                {!hideColumnsSettings && !isFetching && (
                    <ColumnsSettingsItem
                        changeStyle={hideFilters}
                        columnsDefs={columnsDefs}
                        setColumnHide={setColumnHide}
                        panelPosition={settingsPanelPosition}
                    />
                )}
            </div>
        </div>
    );
};

export default forwardRef(SettingsPanel);
